// ═══════════════════════════════════════════════════════════════════
//  DOUBLE-BOOKING OVERRIDE
//
//  An administrator (`appointments:manage`) booking a host on top of an
//  appointment they already have, on purpose and with a reason. Offered on
//  the staff booking, the reassignment and the host reschedule, and only
//  after the server has said the host is busy: 409 with code
//  "hostAlreadyBooked". A 409 "slotUnavailable" is a different thing — a
//  race, or a host off shift — and is never offered the override.
// ═══════════════════════════════════════════════════════════════════

/** The 409 code for "the host already has an appointment then" — DoubleBookingOverrideRequest.HostAlreadyBookedCode. */
export const HOST_ALREADY_BOOKED_CODE = "hostAlreadyBooked"

/** The server's cap on the reason: the DoubleBookingOverrideReason column. */
export const MAX_OVERRIDE_REASON_LENGTH = 500

/** Whether a failed request is the server saying the host is already booked — the refusal the override is for. */
export function isHostAlreadyBooked(error: unknown): boolean {
  const failure = error as { status?: number | string; data?: unknown } | undefined
  const code = (failure?.data as { code?: unknown } | undefined)?.code
  return failure?.status === 409 && code === HOST_ALREADY_BOOKED_CODE
}

/** Whether a reason would pass the server's check: something other than whitespace, within the cap. */
export function isOverrideReasonValid(reason: string): boolean {
  const trimmed = reason.trim()
  return trimmed.length > 0 && trimmed.length <= MAX_OVERRIDE_REASON_LENGTH
}

/** The override as the request bodies take it, or nothing when it is not being used. */
export function doubleBookingFields(armed: boolean, reason: string): { overrideDoubleBooking?: true; overrideReason?: string } {
  return armed ? { overrideDoubleBooking: true, overrideReason: reason.trim() } : {}
}

/** The override's fields as the booking read model carries them (`BookingStatusView`). */
export interface DoubleBookingOverrideView {
  doubleBookingOverride?: boolean
  doubleBookingOverrideBy?: string | null
  doubleBookingOverrideReason?: string | null
}

/**
 * The tooltip on a booking's "Double-booked (override)" badge: who, and why. Undefined for a booking
 * that is not one.
 */
export function describeDoubleBookingOverride(view: DoubleBookingOverrideView): string | undefined {
  if (!view.doubleBookingOverride) return undefined

  const who = view.doubleBookingOverrideBy ? ` by ${view.doubleBookingOverrideBy}` : ""
  const why = view.doubleBookingOverrideReason?.trim()
  return `Double-booked on purpose${who}: the host has another appointment at this time.${why ? ` Reason: ${why}` : ""}`
}
